import { EmailAuthProvider, getAuth, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import HttpClient from '../../../core/HttpClient';
import Failure from '../../../core/Failure';

export interface ChangeRequiredPasswordRequest {
  newPassword: string;
  currentPassword?: string; // the mailed password, only once Firebase asked for a recent login
}

export interface ChangeRequiredPasswordResponse {
  success: boolean;
  currentPasswordRequired?: boolean; // login too old: ask for the mailed password and retry
  error?: string;
}

/**
 * A password sent by mail must be replaced before using the portal, never during a login as.
 * `hasChangedPassword`: done in this session, while a reloaded user doc may still carry the marker.
 */
export const isPasswordChangeRequired = (
  passwordChangeRequiredAt: number | null | undefined,
  isImpersonating: boolean,
  hasChangedPassword: boolean
): boolean => !!passwordChangeRequiredAt && !isImpersonating && !hasChangedPassword;

const getErrorMessage = (code?: string): string => {
  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Le mot de passe reçu par mail est incorrect.';
    case 'auth/weak-password':
      return 'Ce mot de passe est trop faible, choisissez-en un plus long.';
    default:
      return 'Impossible de changer le mot de passe, réessayez.';
  }
};

/**
 * Replace the password sent by mail (LEED-2314): change it in the current session, then clear the marker.
 */
async function changeRequiredPassword(
  request: ChangeRequiredPasswordRequest
): Promise<ChangeRequiredPasswordResponse> {
  const currentUser = getAuth().currentUser;

  try {
    if (!currentUser?.email) {
      throw new ChangeRequiredPasswordFailure('Utilisateur non connecté', 'not-authenticated');
    }

    if (request.currentPassword) {
      await reauthenticateWithCredential(
        currentUser,
        EmailAuthProvider.credential(currentUser.email, request.currentPassword)
      );
    }
    await updatePassword(currentUser, request.newPassword);
  } catch (e) {
    const code = (e as { code?: string })?.code;
    if (code === 'auth/requires-recent-login') {
      return { success: false, currentPasswordRequired: true };
    }
    console.error('Error changing the required password:', e);
    return {
      success: false,
      error: e instanceof ChangeRequiredPasswordFailure ? e.message : getErrorMessage(code),
    };
  }

  // The password is changed even if this write fails: the next login only asks again.
  try {
    await HttpClient.put('/user', { uid: currentUser.uid, password_change_required_at: null }, await currentUser.getIdToken());
  } catch (e) {
    console.error('Error clearing password_change_required_at:', e);
  }

  return { success: true };
}

export class ChangeRequiredPasswordFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default changeRequiredPassword;